import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import DynamicTable from "../../../../components/lab-dynamic-table";
import {
  getDefaultRecordDeleteTooltip,
  getDefaultRecordEditTooltip,
  isDefaultLabRecord,
} from "../../utils/defaultRecordActions";

import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";

const LabDepartment: React.FC<any> = () => {
  const [editData, setEditData] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState<any>("");
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [dosageData, setDosageData] = useState<any>();
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    departmentName: "",
    description: "",
  });
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInput]);

  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "description") {
      setFormData((prevState: any) => ({
        ...prevState,
        [name]: value,
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/lab-department/getAllByPagination`,
        {
          params: {
            pageNo: page + 1,
            count: rowsPerPage,
            search: searchInput,
          },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDosageData(response.data?.data);
      setTotal(response.data?.totalCount);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditClick = (id: any, rowData: any) => {
    setEditData(id);
    setFormData({
      departmentName: rowData?.labDepartmentName,
      description: rowData?.description,
    });
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, debouncedSearch]);

  const handleSubmit = async () => {
    if (editData) {
      try {
        const response = await axios.patch(
          `${process.env.REACT_APP_LAB_URL}/lab-department/updateById/${editData}`,
          {
            labDepartmentName: formData?.departmentName,
            description: formData?.description,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message);
        } else {
          toast.success(response?.data?.message);
          setFormData({
            departmentName: "",
            description: "",
          });
          fetchData();
          setEditData(null);
        }
      } catch (error: any) {
        toast.error(error.message || "Something went wrong");
      }
    } else {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_LAB_URL}/lab-department/addDepartment`,

          {
            labDepartmentName: formData?.departmentName,
            description: formData?.description,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (!response?.data?.isSuccess) {
          toast.error(response?.data?.message);
        } else {
          toast.success(response?.data?.message);
          setFormData({
            departmentName: "",
            description: "",
          });
          fetchData();
          setEditData(null);
        }
      } catch (error: any) {
        toast.error(error.message || "Something went wrong");
      }
    }
  };


  // excel file upload
  const handleExcelFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (!["xls", "xlsx"].includes(fileExtension || "")) {
      toast.error("Please upload a valid Excel file (.xls or .xlsx)");
      e.target.value = ""; // reset input if invalid
      return;
    }

    const data = new FormData();
    data.append("file", file);

    try {
      setUploading(true); // ✅ start loader

      const res = await axios.post(
        `${process.env.REACT_APP_LAB_URL}/import-data/fileUpload`,
        data,
        {
          params: {entity: "labDepartment"},
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(res.data?.message || "File uploaded successfully");

      e.target.value = ""; // reset input
    } catch (error: any) {
      console.error("❌ Error uploading excel file", error);
      toast.info(
        error.response?.data?.message ||
          error.message ||
          "Failed to upload file"
      );
      e.target.value = "";
    } finally {
      setUploading(false); // ✅ stop loader
    }
  };


  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteClick = (id: any) => {
    setIsDeleteModalOpen(true);
    setSelectedItemId(id);
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authorization token is missing");
        return;
      }

      const response = await axios.delete(
        `${process.env.REACT_APP_LAB_URL}/lab-department/deleteById/${selectedItemId}`,

        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      fetchData();
      toast.success(response.data.message);
      setFormData({
        departmentName: "",
        description: "",
      });
      setEditData(null);
    } catch (error: any) {
      toast.error(error.message);
    }
    setIsDeleteModalOpen(false);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 2,
          padding: "1px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: {
              xs: "1rem",
              sm: "1.25rem",
              md: "1.5rem",
            },
          }}
        >
          Lab Department
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label="Search"
            placeholder="Search Department..."
            variant="outlined"
            value={searchInput}
            // className="search-field"
            className="custom-input"
            onChange={handleSearchInputChange}
            size="small"
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#055FFC" }} />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={1}>
        <Grid item xs={12} sm={4} md={4}>
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: 2,
              p: 3,
              boxShadow: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {editData ? "Update Department" : "Add New Department"}
            </Typography>
            <TextField
              size="small"
              // className="search-field"
              className="custom-input"
              label="Department Name"
              name="departmentName"
              value={formData?.departmentName}
              fullWidth
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <TextField
              size="small"
              label="Description"
              // className="search-field"
              name="description"
              className="custom-input"
              type="string"
              value={formData?.description || ""}
              fullWidth
              onChange={handleChange}
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={!formData?.departmentName}
            >
              {editData ? "Update" : "Save"}
            </Button>

            <div className="w-full mt-4 flex justify-between items-center gap-2">
              <div>
                <input
                  type="file"
                  accept=".xls,.xlsx"
                  id="excel-upload"
                  onChange={handleExcelFileUpload}
                  style={{ display: "none" }} // hide the default file input
                />
                <label htmlFor="excel-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    disabled={uploading} // ✅ disable while uploading
                    startIcon={
                      uploading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <img
                          src="/excel-logo.png"
                          alt="Excel File"
                          style={{ width: 24, height: 24 }}
                        />
                      )
                    }
                    sx={{
                      textTransform: "none",
                      borderRadius: "10px",
                      padding: "8px 16px",
                    }}
                  >
                    {uploading
                      ? "Uploading..."
                      : "Import Excel File (.xls / .xlsx)"}
                  </Button>
                </label>
              </div>
              <Tooltip arrow placement="top" title="Download Excel Template">
                <IconButton
                  sx={{
                    // backgroundColor: "blue",
                    color: "white",
                    "&:hover": {
                      // backgroundColor: "darkgreen",
                    },
                  }}
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = "/LabDepartment Template.xlsx";
                    link.download = "LabDepartment Template.xlsx";
                    link.click();
                  }}
                >
                  {/* <DescriptionIcon /> */}
                  <img
                    src="/export-excel.png" // ✅ directly from public folder
                    alt="Excel File"
                    style={{ width: 30, height: 30 }}
                  />
                </IconButton>
              </Tooltip>
            </div>
          </Box>
        </Grid>

        <Grid item xs={12} sm={8} md={8}>
          <DynamicTable
            headers={["SN", "Department Name", "Description", "Actions"]}
            data={dosageData?.map((item: any, index: any) => [
              page * rowsPerPage + index + 1,
              item?.labDepartmentName || "",
              `${item?.description}` || "",
            ])}
            loading={loading}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRows={total}
            actionButtons={[
              {
                icon: <EditIcon sx={{ color: "#FFB800", cursor: "pointer" }} />,
                onClick: (rowIndex: any) =>
                  handleEditClick(
                    dosageData[rowIndex]?._id,
                    dosageData[rowIndex]
                  ),
                disabled: (rowIndex) => isDefaultLabRecord(dosageData[rowIndex]),
                tooltip: (rowIndex) =>
                  getDefaultRecordEditTooltip(dosageData[rowIndex]),
              },
              {
                icon: (
                  <DeleteIcon sx={{ color: "#FF5454", cursor: "pointer" }} />
                ),
                onClick: (rowIndex: any) =>
                  handleDeleteClick(dosageData[rowIndex]?._id),
                disabled: (rowIndex) => isDefaultLabRecord(dosageData[rowIndex]),
                tooltip: (rowIndex) =>
                  getDefaultRecordDeleteTooltip(dosageData[rowIndex]),
              },
            ]}
            height={"60vh"}
          />
          {dosageData === null && (
            <h1
              style={{
                color: "gray",
                marginTop: "8px",
                textAlign: "center",
                fontSize: "12px",
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              No data found
            </h1>
          )}
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default LabDepartment;
